import React, { useState } from 'react';
import { submitEnquiry } from './visitor';
import { WHATSAPP_NUMBER, waNumber } from '../config';

// Shared "Contact Me" lead-capture form. ALWAYS captures the visitor's phone —
// that is the whole point: a scanned QR or a card tap should end with a reachable
// number in the CRM.
//
//   handoff=true  (digital card): after capturing, also opens WhatsApp on the
//                 rep's number so the visitor can start chatting immediately.
//   handoff=false (microsite): pure capture — no WhatsApp, no call. Shows a
//                 thank-you so the visitor knows their details went through.
//
// Props: slug, whatsapp (rep number, handoff only), source ('card'|'microsite'),
//        handoff, presetInterest (e.g. a product line from "Get Quote"), onClose.
export default function ContactModal({ slug, whatsapp, source = 'card', handoff = true, presetInterest = '', onClose }) {
  const REMARKS_MAX = 100;
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [remarks, setRemarks] = useState('');
  const [agree, setAgree] = useState(false);   // affirmative opt-in — never pre-ticked
  const [done, setDone] = useState(false);
  // Phone is mandatory: it is the only reliable way to reach the visitor later.
  const phoneDigits = phone.replace(/[^\d]/g, '');
  const phoneOk = phoneDigits.length >= 8 && phoneDigits.length <= 15;
  const ok = name.trim() && company.trim() && phoneOk && agree;

  const submit = () => {
    if (!ok) return;
    if (handoff) {
      // Fold city + remarks into the WhatsApp opener so the rep sees them at once.
      const extra = [city.trim() && `City: ${city.trim()}`, remarks.trim() && `Remarks: ${remarks.trim()}`]
        .filter(Boolean).join('. ');
      const text = `Hi, I'm ${name.trim()} from ${company.trim()}. I found your TECGSOFT card and would like to connect.${extra ? ` ${extra}.` : ''}`;
      // open WhatsApp synchronously (before any await) to dodge pop-up blockers
      window.open(`https://wa.me/${whatsapp ? waNumber(whatsapp) : WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`, '_blank');
    }
    submitEnquiry({
      employeeSlug: slug, name: name.trim(), company: company.trim(),
      phone: phone.trim(), city: city.trim() || undefined, message: remarks.trim() || undefined,
      interest: presetInterest || undefined,
      source, consent: true,
    }).catch(() => {});
    if (handoff) onClose(); else setDone(true);
  };

  const inp = { width: '100%', padding: '12px 14px', marginTop: 6, borderRadius: 12, border: '1px solid rgba(201,162,75,.3)', background: 'rgba(255,255,255,.06)', color: '#fff', fontSize: 15, outline: 'none' };
  const lbl = { fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#fff', fontWeight: 700 };
  const shell = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 50, fontFamily: "'Poppins',-apple-system,'Segoe UI',sans-serif" };
  const panel = { width: '100%', maxWidth: 360, background: 'linear-gradient(160deg,#2b2d31,#151619)', border: '1px solid rgba(201,162,75,.3)', borderRadius: 22, padding: 24, color: '#eaf3ee', boxShadow: '0 30px 80px rgba(0,0,0,.6)' };

  if (done) {
    return (
      <div style={shell} onClick={onClose}>
        <div style={{ ...panel, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
          <div style={{ fontSize: 19, fontWeight: 700, color: '#fff' }}>Thank you!</div>
          <div style={{ fontSize: 13, color: '#9fc0b2', margin: '6px 0 18px' }}>Your details have been shared with our team — we'll be in touch shortly.</div>
          <button onClick={onClose} style={{ width: '100%', padding: 12, borderRadius: 12, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14.5, color: '#1b1c1f', background: 'linear-gradient(135deg,#e6c66a,#c9a24b)' }}>Close</button>
        </div>
      </div>
    );
  }

  const cta = handoff ? 'Send Hello on WhatsApp' : 'Submit';
  const subtitle = handoff ? "Leave your details — we'll connect on WhatsApp." : "Leave your details and our team will get in touch.";

  return (
    <div style={shell} onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 19, fontWeight: 700, color: '#fff' }}>Contact Me</div>
        <div style={{ fontSize: 12.5, color: '#fff', margin: '4px 0 16px' }}>{subtitle}</div>
        <label style={lbl}>Name</label>
        <input style={inp} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" autoFocus />
        <div style={{ height: 12 }} />
        <label style={lbl}>Business / Clinic</label>
        <input style={inp} value={company} onChange={(e) => setCompany(e.target.value)} placeholder="e.g. Apollo Clinic" />
        <div style={{ height: 12 }} />
        <label style={lbl}>Phone Number</label>
        <input style={inp} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210" />
        <div style={{ height: 12 }} />
        <label style={lbl}>City <span style={{ opacity: .6, textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>(optional)</span></label>
        <input style={inp} value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Chennai" />
        <div style={{ height: 12 }} />
        <label style={lbl}>Remarks <span style={{ opacity: .6, textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>(optional)</span></label>
        <textarea style={{ ...inp, minHeight: 64, resize: 'none', fontFamily: 'inherit' }} value={remarks}
                  maxLength={REMARKS_MAX} rows={3}
                  onChange={(e) => setRemarks(e.target.value.slice(0, REMARKS_MAX))}
                  placeholder="Anything you'd like us to know" />
        <div style={{ fontSize: 11, color: '#9fc0b2', textAlign: 'right', marginTop: 4 }}>{remarks.length}/{REMARKS_MAX}</div>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginTop: 16, cursor: 'pointer', fontSize: 12.5, lineHeight: 1.5, color: '#eaf3ee' }}>
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                 style={{ width: 17, height: 17, marginTop: 1, accentColor: '#c9a24b', flex: 'none' }} />
          <span>I agree to be contacted by TECGSOFT and to the{' '}
            <a href="/terms" target="_blank" rel="noreferrer" style={{ color: '#e6c66a', fontWeight: 600 }}>Terms &amp; Privacy</a>.
          </span>
        </label>
        <button onClick={submit} disabled={!ok}
          style={{ width: '100%', marginTop: 18, padding: 13, borderRadius: 12, border: 'none', cursor: ok ? 'pointer' : 'default', fontWeight: 700, fontSize: 14.5, color: '#1b1c1f', background: 'linear-gradient(135deg,#e6c66a,#c9a24b)', opacity: ok ? 1 : 0.5 }}>
          {cta}
        </button>
        <button onClick={onClose} style={{ width: '100%', marginTop: 8, padding: 10, borderRadius: 12, border: '1px solid rgba(255,255,255,.15)', background: 'transparent', color: '#d5d9df', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
      </div>
    </div>
  );
}
